import { reportErrorToConsole } from '@src/services/error-logger'
import { apiClient } from '@src/services/http-common'
import { DevToolsCommandExecution, DevToolsCommandType } from '@src/types/dev-tools'
import { useQuery } from '@tanstack/react-query'

const RECENT_HISTORY_ROUTE = 'dev-tools/command-history/recent'
const PAGED_HISTORY_ROUTE = 'dev-tools/command-history'

const DEV_TOOLS_HISTORY_SCOPE = 'devToolsHistory' as const

export const devToolsHistoryKeys = {
  all: [DEV_TOOLS_HISTORY_SCOPE] as const,
  recent: (commandType: DevToolsCommandType, limit: number) =>
    [...devToolsHistoryKeys.all, 'recent', commandType, limit] as const,
  paged: (commandType: DevToolsCommandType, offset: number, limit: number) =>
    [...devToolsHistoryKeys.all, 'paged', commandType, offset, limit] as const,
}

const fetchRecentExecutions = async (
  commandType: DevToolsCommandType,
  limit: number,
): Promise<DevToolsCommandExecution[]> => {
  try {
    const response = await apiClient.get<DevToolsCommandExecution[]>(RECENT_HISTORY_ROUTE, {
      params: { commandType, limit },
    })
    return response.data
  } catch (error) {
    reportErrorToConsole(error)
    throw error
  }
}

const fetchPagedExecutions = async (
  commandType: DevToolsCommandType,
  offset: number,
  limit: number,
): Promise<DevToolsCommandExecution[]> => {
  try {
    const response = await apiClient.get<DevToolsCommandExecution[]>(PAGED_HISTORY_ROUTE, {
      params: { commandType, offset, limit },
    })
    return response.data
  } catch (error) {
    reportErrorToConsole(error)
    throw error
  }
}

export const useRecentCommandHistory = (commandType: DevToolsCommandType, limit: number) => {
  return useQuery({
    queryKey: devToolsHistoryKeys.recent(commandType, limit),
    queryFn: () => fetchRecentExecutions(commandType, limit),
  })
}

export const usePagedCommandHistory = (commandType: DevToolsCommandType, offset: number, limit: number) => {
  return useQuery({
    queryKey: devToolsHistoryKeys.paged(commandType, offset, limit),
    queryFn: () => fetchPagedExecutions(commandType, offset, limit),
  })
}
